import { useEffect, useState } from "react";
import { toast } from "sonner";
import { createCheckout } from "@/lib/api";

const EVENT_DATE = new Date("2026-09-13T09:00:00+02:00");
const PRODUCT_ID = "combo_duello"; // €129 – listino €258 – 50% off

// Meta Pixel helper (usa env var, no-op se assente).
const trackPixel = (event, params = {}) => {
    if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", event, params);
    }
};

function useCountdown(target) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const diff = Math.max(0, target.getTime() - now);
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff / 3600000) % 24);
    const m = Math.floor((diff / 60000) % 60);
    const s = Math.floor((diff / 1000) % 60);
    return { d, h, m, s, expired: diff === 0 };
}

export default function OffertaLancio() {
    const cd = useCountdown(EVENT_DATE);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        trackPixel("PageView");
        trackPixel("ViewContent", { content_ids: [PRODUCT_ID], content_type: "product", value: 129, currency: "EUR" });
    }, []);

    const buy = async (audience) => {
        trackPixel("AddToCart", { content_ids: [PRODUCT_ID], value: 129, currency: "EUR", audience });
        setLoading(true);
        try {
            const { checkout_url } = await createCheckout({ product_id: PRODUCT_ID, quantity: 1, origin_url: window.location.origin });
            window.location.href = checkout_url;
        } catch (e) {
            toast.error("Errore checkout — riprova o contatta il supporto.");
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0b0d] text-[#F4F2ED]" data-testid="page-offerta-lancio">
            {/* HERO */}
            <section className="relative overflow-hidden border-b-4 border-[#E2231A]">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a0b0d] via-[#15181C] to-[#1a0708] opacity-95" />
                <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(226,35,26,0.15) 0%, transparent 40%)" }} />
                <div className="relative max-w-5xl mx-auto px-5 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28 text-center">
                    <div className="inline-flex items-center gap-2 border border-[#E2231A]/60 bg-[#E2231A]/10 px-4 py-2 text-[10px] md:text-xs uppercase tracking-widest mb-6" data-testid="badge-launch">
                        <span className="w-2 h-2 bg-[#E2231A] rounded-full animate-pulse"></span>
                        OFFERTA LANCIO · POSTI LIMITATI
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl md:text-7xl uppercase leading-none mb-4 tracking-tight" data-testid="hero-title">
                        🇯🇵 <span className="text-[#E2231A]">Guida una EVO X o WRX</span><br/>in pista a <span className="text-[#E2231A]">-50%</span>
                    </h1>
                    <p className="text-base md:text-xl text-[#c8ccd1] mt-6" data-testid="hero-subtitle">
                        Pacchetto <s className="text-[#9AA2AD]">€258</s> a soli <b className="text-[#F4F2ED] text-2xl md:text-3xl">€129</b> ·
                        Paghi in <b>3 rate da €43</b> con <span className="text-[#EA5AC0]">Klarna</span>
                    </p>

                    {/* COUNTDOWN */}
                    {!cd.expired && (
                        <div className="mt-10 grid grid-cols-4 gap-2 md:gap-4 max-w-lg mx-auto" data-testid="countdown">
                            {[["Giorni", cd.d], ["Ore", cd.h], ["Min", cd.m], ["Sec", cd.s]].map(([lbl, val]) => (
                                <div key={lbl} className="border border-[#E2231A]/40 bg-[#15181C] py-3 md:py-5">
                                    <div className="font-display text-3xl md:text-5xl text-[#E2231A]">{String(val).padStart(2, "0")}</div>
                                    <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-[#9AA2AD] mt-1">{lbl}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="mt-4 text-xs uppercase tracking-widest text-[#9AA2AD]">Countdown al 13 Settembre 2026</div>
                </div>
            </section>

            {/* DUE PORTE */}
            <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20">
                <div className="grid md:grid-cols-2 gap-4 md:gap-8">
                    {/* PER LUI */}
                    <div className="border border-[#9AA2AD]/20 bg-[#15181C] p-6 md:p-10 flex flex-col" data-testid="card-per-lui">
                        <div className="text-[10px] uppercase tracking-widest text-[#E2231A] mb-3">PER LUI</div>
                        <h2 className="font-display text-2xl md:text-4xl uppercase leading-tight mb-4">
                            Hai sempre <span className="text-[#E2231A]">sognato</span> di guidare una EVO X?
                        </h2>
                        <p className="text-[#c8ccd1] mb-6 flex-1">
                            Motore turbo, trazione integrale, 4 curve chiuse in pista sicura, con istruttore accanto. Nessuna cauzione, nessun rischio. Solo adrenalina vera.
                        </p>
                        <button
                            onClick={() => buy("him")}
                            disabled={loading}
                            data-testid="btn-cta-per-lui"
                            className="bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-50 text-[#F4F2ED] py-4 uppercase tracking-widest font-display text-lg transition-colors"
                        >{loading ? "Caricamento…" : "🏁 Voglio Guidare"}</button>
                    </div>
                    {/* PER LEI */}
                    <div className="border border-[#EA5AC0]/30 bg-gradient-to-br from-[#15181C] to-[#1a0e1c] p-6 md:p-10 flex flex-col" data-testid="card-per-lei">
                        <div className="text-[10px] uppercase tracking-widest text-[#EA5AC0] mb-3">PER LEI</div>
                        <h2 className="font-display text-2xl md:text-4xl uppercase leading-tight mb-4">
                            Il <span className="text-[#EA5AC0]">regalo</span> perfetto per il tuo lui
                        </h2>
                        <p className="text-[#c8ccd1] mb-6 flex-1">
                            Sorprendilo con un'esperienza che non dimenticherà. Ricevi il voucher via email in 2 minuti — lo apre lui quando vuole tu.
                        </p>
                        <a
                            href="/voucher?tier=combo"
                            data-testid="btn-cta-per-lei"
                            className="text-center bg-[#EA5AC0] hover:bg-[#D148A9] text-[#F4F2ED] py-4 uppercase tracking-widest font-display text-lg transition-colors"
                        >🎁 Regala Ora</a>
                    </div>
                </div>
            </section>

            {/* COSA INCLUDE */}
            <section className="border-y border-[#9AA2AD]/15 bg-[#0f1013]">
                <div className="max-w-5xl mx-auto px-5 md:px-8 py-16">
                    <h3 className="font-display text-3xl md:text-4xl uppercase text-center mb-10">Cosa <span className="text-[#E2231A]">Include</span></h3>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            { icon: "🏎", title: "Guida entrambe le auto", text: "EVO X + WRX in pista chiusa al traffico" },
                            { icon: "🛡", title: "Zero cauzione", text: "Nessun deposito, nessun rischio patrimoniale" },
                            { icon: "👨‍🏫", title: "Istruttore in auto", text: "Sicurezza + tecnica con il pilota accanto" },
                            { icon: "📸", title: "Certificato + Boarding Pass", text: "Documento digitale del tuo turno" },
                            { icon: "🎥", title: "Video onboard disponibile", text: "Add-on opzionale per rivivere l'esperienza" },
                            { icon: "🇯🇵", title: "Vera atmosfera JDM", text: "Auto giapponesi cult curate nel dettaglio" },
                        ].map(f => (
                            <div key={f.title} className="border-l-2 border-[#E2231A] pl-4">
                                <div className="text-2xl mb-2">{f.icon}</div>
                                <div className="font-display text-sm uppercase tracking-widest mb-1">{f.title}</div>
                                <div className="text-sm text-[#9AA2AD]">{f.text}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* KLARNA */}
            <section className="max-w-4xl mx-auto px-5 md:px-8 py-16">
                <div className="border border-[#EA5AC0]/30 bg-gradient-to-br from-[#1a0e1c] to-[#0f1013] p-6 md:p-10" data-testid="section-klarna">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                        <div className="bg-[#EA5AC0] px-6 py-3 text-[#0a0b0d] font-black text-xl tracking-tight rounded">Klarna.</div>
                        <div className="flex-1">
                            <h3 className="font-display text-2xl uppercase mb-3">Paga in <span className="text-[#EA5AC0]">3 rate</span> senza interessi</h3>
                            <p className="text-[#c8ccd1] mb-3">
                                <b>€43 oggi</b> · <b>€43 tra 30gg</b> · <b>€43 tra 60gg</b>. Nessun costo aggiuntivo, decisione istantanea al checkout. Servizio offerto da Klarna, uno dei leader europei del "Buy Now Pay Later".
                            </p>
                            <div className="text-xs uppercase tracking-widest text-[#9AA2AD]">Disponibile al checkout · Verifica idoneità automatica</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PERCHÉ ORA */}
            <section className="max-w-4xl mx-auto px-5 md:px-8 pb-16">
                <div className="border-2 border-[#E2231A] bg-gradient-to-br from-[#E2231A]/10 to-[#1a0708] p-8 text-center">
                    <div className="text-[11px] uppercase tracking-widest text-[#E2231A] mb-3">⚠ Perché ora?</div>
                    <h3 className="font-display text-2xl md:text-3xl uppercase mb-4 leading-tight">
                        Solo <span className="text-[#E2231A]">30 posti</span> disponibili. Fine offerta al sold-out.
                    </h3>
                    <p className="text-[#c8ccd1] text-sm mb-6">
                        L'evento è a Battipaglia (SA) il 13 Settembre 2026. La finestra a 50% è un lancio unico — dopo il sold-out l'esperienza torna al listino di €258.
                    </p>
                    <button
                        onClick={() => buy("perche-ora")}
                        disabled={loading}
                        data-testid="btn-cta-perche-ora"
                        className="bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-50 text-[#F4F2ED] py-4 px-10 uppercase tracking-widest font-display text-lg transition-colors"
                    >{loading ? "…" : "🏁 Prendi il tuo posto · €129"}</button>
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-3xl mx-auto px-5 md:px-8 pb-20">
                <h3 className="font-display text-2xl md:text-3xl uppercase text-center mb-8">FAQ</h3>
                <div className="space-y-4" data-testid="faq-list">
                    <FaqItem q="Serve la patente sportiva?" a="No. Basta una normale patente B in corso di validità, presentata al check-in in pista." />
                    <FaqItem q="E se piove?" a="L'evento si tiene comunque su pista sicura. In caso di condizioni estreme che impediscano la guida, ti ricontattiamo per una data alternativa o il rimborso completo." />
                    <FaqItem q="Klarna funziona davvero?" a="Sì, Klarna è integrato nel nostro checkout Stripe. Al momento del pagamento seleziona 'Klarna': in 30 secondi verifichi idoneità e paghi €43 subito. Le altre 2 rate partono automaticamente a 30 e 60 giorni. Zero interessi." />
                </div>
            </section>

            {/* FOOTER */}
            <footer className="border-t border-[#9AA2AD]/20 bg-[#0a0b0d]">
                <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 text-center text-xs text-[#9AA2AD]">
                    <img src="/logo/velora-horizontal-alt.png" alt="VELORA JDM" className="h-10 mx-auto mb-3" />
                    <div className="mb-2">Marchio commerciale di MAS S.r.l. · P.IVA 01688990629 · Foglianise (BN)</div>
                    <div className="flex gap-4 justify-center flex-wrap">
                        <a href="/" className="hover:text-[#F4F2ED]">Sito principale</a>
                        <a href="/termini" className="hover:text-[#F4F2ED]">Termini</a>
                        <a href="/privacy" className="hover:text-[#F4F2ED]">Privacy</a>
                        <a href="/contatti" className="hover:text-[#F4F2ED]">Contatti</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border border-[#9AA2AD]/20 bg-[#15181C]">
            <button onClick={() => setOpen(o => !o)} className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-[#1C2026]">
                <span className="font-display uppercase text-sm tracking-widest">{q}</span>
                <span className="text-[#E2231A] text-xl">{open ? "−" : "+"}</span>
            </button>
            {open && <div className="px-5 pb-4 text-sm text-[#c8ccd1]">{a}</div>}
        </div>
    );
}
